/**
 * StandardToggle — segmented control to pick the frequency-rate reporting
 * standard used for TRIR / LTIFR / LWC / MTC / near-miss rates:
 *   OSHA   → per 200,000 hours (29 CFR 1904)
 *   Indian → per 1,000,000 man-hours (IS 3786:1983 / ILO)
 *
 * The choice is a per-user UI preference (persisted in localStorage) and is
 * threaded into every rate request via DashboardContext. It only rescales rate
 * metrics; counts are unaffected.
 */
import React from 'react';

export const STANDARD_STORAGE_KEY = 'saarv_safety_standard';

export const STANDARD_OPTIONS = [
  { id: 'osha', label: 'OSHA', hint: 'per 200,000 hrs (29 CFR 1904)' },
  { id: 'india', label: 'Indian', hint: 'per 1,000,000 man-hrs (IS 3786 / ILO)' },
];

/** Read the persisted standard (defaults to OSHA). */
export function getStoredStandard() {
  try {
    const v = localStorage.getItem(STANDARD_STORAGE_KEY);
    return v === 'india' ? 'india' : 'osha';
  } catch {
    return 'osha';
  }
}

export default function StandardToggle({ value, onChange }) {
  const set = (id) => {
    try { localStorage.setItem(STANDARD_STORAGE_KEY, id); } catch { /* ignore */ }
    onChange?.(id);
  };

  return (
    <div
      className="inline-flex items-center rounded-md border border-gray-300 bg-white p-0.5"
      role="group"
      aria-label="Rate reporting standard"
      title="Frequency-rate standard — rescales TRIR/LTIFR/LWC/MTC/near-miss"
    >
      {STANDARD_OPTIONS.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => set(opt.id)}
            aria-pressed={active}
            title={opt.hint}
            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              active ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
